import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import SeoMeta from './components/SeoMeta';
import Header from './components/header';
import Navbar from './components/Navbar';
import styles from './App.module.css';
import './App.css';
import './i18n';

// ✅ Lazy load components that aren't immediately visible
const WhyChooseUs = lazy(() => import('./components/whyChooseUs'));
const Services = lazy(() => import('./components/services'));
const ServiceAreas = lazy(() => import('./components/ServiceAreas'));
const TrustBadges = lazy(() => import('./components/TrustBadges'));
const CTABanner = lazy(() => import('./components/CTABanner'));
const Feedback = lazy(() => import('./components/feedback'));
const FAQ = lazy(() => import('./components/FAQ'));
const Blog = lazy(() => import('./components/Blog'));
const Team = lazy(() => import('./components/team'));
const Contact = lazy(() => import('./components/contact'));
const Location = lazy(() => import('./components/location'));
const ImageSlider = lazy(() => import('./components/ImageSlider'));

// Loading component
const LoadingFallback = () => (
  <div style={{ 
    textAlign: 'center', 
    padding: '2rem',
    color: 'var(--text-color)'
  }}>
    Loading...
  </div>
);

export default function App() {
  const { i18n } = useTranslation();

  // Determine initial language: prefer URL path (/ar) then saved preference
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [language, setLanguage] = useState(() => {
    try {
      const pathLang = (typeof window !== 'undefined' && window.location && window.location.pathname && window.location.pathname.startsWith('/ar')) ? 'ar' : null;
      return pathLang || localStorage.getItem('language') || 'en';
    } catch (e) {
      return localStorage.getItem('language') || 'en';
    }
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('language', language);
    i18n.changeLanguage(language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language === 'ar' ? 'ar' : 'en';
  }, [language, i18n]);

  return (
    <div className={`${styles.app} ${darkMode ? styles.dark : ''}`}>
      <SeoMeta lang={language} />
      
      <Navbar darkMode={darkMode} />

      <div className={styles.sideButtons}>
        <button 
          className={styles.modeBtn} 
          onClick={() => setDarkMode(!darkMode)}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {darkMode ? "☀️" : "🌙"}
        </button>

        {language === "en" ? (
          <button 
            className={styles.langBtn} 
            onClick={() => setLanguage("ar")}
            aria-label="Switch to Arabic"
          >
            <img src="/flags/saudi.svg" alt="العربية" />
          </button>
        ) : (
          <button 
            className={styles.langBtn} 
            onClick={() => setLanguage("en")}
            aria-label="Switch to English"
          >
            <img src="/flags/usa.svg" alt="English" />
          </button>
        )}
      </div>

      {/* ✅ Components above the fold - load immediately */}
      <div id="home"><Header /></div>
      
      {/* ✅ Wrap lazy-loaded components in Suspense */}
      <Suspense fallback={<LoadingFallback />}>
        <div id="slider"><ImageSlider /></div>
        <CTABanner />
        <div id="services"><Services /></div>
        <div id="areas"><ServiceAreas /></div>
        <div id="trust"><TrustBadges /></div>
        <div id="why"><WhyChooseUs /></div>
        <div id="location"><Location /></div>
        <div id="feedback"><Feedback /></div>
        <div id="faq"><FAQ /></div>
        <div id="blog"><Blog /></div>
        <div id="team"><Team /></div>
        <div id="contact"><Contact /></div>
      </Suspense>

      <a 
        href="tel:+966555632801" 
        className={styles.floatingCall}
        aria-label="Call Al Faraj Towing"
      >
        📞 Call Now
      </a>
      
      <a
        href="https://wa.me/966555632801"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.floatingWhatsApp}
        aria-label="Chat on WhatsApp"
      >
        💬 Chat on WhatsApp
      </a>

      <footer className={styles.footer}>
        © {new Date().getFullYear()} Al Faraj Towing Services. All Rights Reserved.
      </footer>
    </div>
  );
}